import { apiClient } from './apiClient';
import { logger } from '../../utils/logger';

// Creator types for the mobile app
export interface Creator {
  id: string;
  username: string;
  firstName: string;
  lastName: string;
  avatar?: string;
  bio: string;
  followers: number;
  recipes: number;
  rating: number;
  reviewCount: number;
  likes: number;
  views: number;
  isVerified: boolean;
  specialties: string[];
  experience_years: number;
  socialLinks: {
    youtube?: string;
    instagram?: string;
    twitter?: string;
    tiktok?: string;
    website?: string;
    linkedin?: string;
  };
  certifications: string[];
  awards: string[];
}

export interface CreatorListParams {
  page?: number;
  size?: number;
  search?: string;
  specialty?: string;
  is_featured?: boolean;
  verified_only?: boolean;
  min_followers?: number;
}

export interface CreatorListResponse {
  creators: Creator[];
  total: number;
  page: number;
  per_page: number;
  total_pages: number;
}

// Transform backend CreatorResponse to mobile app Creator format
// Backend response structure:
// { id, user_id, user_name, user_email, status, tier, commission_rate, is_featured,
//   profile: { id, user_id, bio, website, youtube_channel, instagram, twitter, tiktok,
//              specializations, years_experience, certifications, location, languages,
//              avatar_url, banner_url, created_at, updated_at },
//   stats: { total_recipes, total_followers, total_likes, total_views, average_rating, ... },
//   created_at, updated_at }
const transformCreatorResponse = (data: any): Creator => {
  const profile = data.profile || {};
  const stats = data.stats || {};

  // Parse user_name to extract first and last name
  const userName = data.user_name || '';
  const nameParts = userName.split('_');
  const firstName = nameParts[0]
    ? nameParts[0].charAt(0).toUpperCase() + nameParts[0].slice(1)
    : '';
  const lastName = nameParts[1]
    ? nameParts[1].charAt(0).toUpperCase() + nameParts[1].slice(1)
    : '';

  return {
    id: String(data.id || data.user_id),
    username: data.user_name || '',
    firstName,
    lastName,
    avatar: profile.avatar_url || undefined,
    bio: profile.bio || '',
    followers: stats.total_followers || 0,
    recipes: stats.total_recipes || 0,
    rating: stats.average_rating || 0,
    reviewCount: stats.total_reviews || 0,
    likes: stats.total_likes || 0,
    views: stats.total_views || 0,
    isVerified: data.status === 'approved',
    specialties: profile.specializations || [],
    experience_years: profile.years_experience || 0,
    socialLinks: {
      youtube: profile.youtube_channel || undefined,
      instagram: profile.instagram || undefined,
      twitter: profile.twitter || undefined,
      tiktok: profile.tiktok || undefined,
      website: profile.website || undefined,
      linkedin: undefined,
    },
    certifications: profile.certifications || [],
    awards: [], // Not in backend response
  };
};

export const creatorApi = {
  // Get list of creators with pagination and filtering
  // Falls back to mock data when API is unavailable
  getCreators: async (
    params: CreatorListParams = {}
  ): Promise<CreatorListResponse> => {
    try {
      const queryParams = new URLSearchParams();

      if (params.page) queryParams.set('page', params.page.toString());
      if (params.size) queryParams.set('size', params.size.toString());
      if (params.search) queryParams.set('search', params.search);

      const response = await apiClient.get(
        `/creators?${queryParams.toString()}`
      );

      // Transform response data
      const backendResponse = response.data;
      const creatorsData =
        backendResponse.creators || backendResponse.items || [];

      return {
        creators: creatorsData.map(transformCreatorResponse),
        total: backendResponse.total || creatorsData.length,
        page: backendResponse.page || params.page || 1,
        per_page: backendResponse.per_page || params.size || 20,
        total_pages:
          backendResponse.total_pages ||
          Math.ceil(
            (backendResponse.total || creatorsData.length) / (params.size || 20)
          ),
      };
    } catch (error: any) {
      logger.error('Creators API failed:', error.message);
      throw error;
    }
  },

  // Get a single creator by ID
  getCreatorById: async (creatorId: string): Promise<Creator> => {
    try {
      const response = await apiClient.get(`/creators/${creatorId}`);
      return transformCreatorResponse(response.data);
    } catch (error: any) {
      logger.error('Creator API failed:', error.message);
      throw error;
    }
  },

  // Get featured creators
  getFeaturedCreators: async (
    params: { page?: number; size?: number } = {}
  ): Promise<CreatorListResponse> => {
    return creatorApi.getCreators({ ...params, is_featured: true });
  },

  // Get creator's profile
  getCreatorProfile: async (userId: string): Promise<any> => {
    try {
      const response = await apiClient.get(`/users/${userId}/profile`);
      return response.data;
    } catch (error: any) {
      logger.error('Failed to fetch creator profile:', error.message);
      throw error;
    }
  },

  // Search creators
  searchCreators: async (
    query: string,
    params: Omit<CreatorListParams, 'search'> = {}
  ): Promise<CreatorListResponse> => {
    return creatorApi.getCreators({ ...params, search: query });
  },
};

export default creatorApi;
